import React from 'react'
import { Control, Controller, ControllerRenderProps } from 'react-hook-form'

import {
  ToggleButton,
  ToggleButtonGroup,
  ToggleButtonGroupProps,
} from '@material-ui/lab'
import { styled, Typography } from '@material-ui/core'

type Option<T> = {
  label: string
  value: T
}

type Props<T> = ToggleButtonGroupProps & {
  control: Control
  name: string
  options: Option<T>[]
  defaultValue?: T
  enforceValueSet?: boolean
  disabled?: boolean
}

const StyledToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
  '& .MuiToggleButtonGroup-grouped': {
    '&.Mui-selected': {
      color: 'black',
    },
    '&.Mui-disabled': {
      color: 'rgba(0, 0, 0, 0.12)',
    },
  },
}))

export function ControlledToggleButtonGroup<T>(
  props: Props<T>,
): React.ReactElement {
  const {
    control,
    name,
    options,
    defaultValue,
    enforceValueSet,
    disabled,
    onChange,
    orientation,
    ...toggleButtonGroupProps
  } = props

  return (
    <Controller
      control={control}
      name={name}
      defaultValue={defaultValue ?? null}
      render={({
        value,
        onChange: controllerOnChange,
      }: Omit<ControllerRenderProps, 'value'> & { value: T }) => (
        <StyledToggleButtonGroup
          orientation={orientation === undefined ? 'horizontal' : orientation}
          {...toggleButtonGroupProps}
          value={value}
          onChange={(event, newValue) => {
            if (enforceValueSet && newValue === null) {
              return
            }

            controllerOnChange(newValue)

            if (onChange) {
              onChange(event, newValue)
            }
          }}
        >
          {options.map((option) => (
            <ToggleButton
              key={option.value as string}
              value={option.value}
              disabled={disabled ?? false}
            >
              <Typography>{option.label}</Typography>
            </ToggleButton>
          ))}
        </StyledToggleButtonGroup>
      )}
    />
  )
}
